import { supabase } from "@/lib/supabase"
import type { WizardSubmissionRow } from "@/lib/supabase"
import type { ProgramSession, Exercise, Warmup, WarmupItem } from "@/types/programs"
import { buildProgrammeId } from "@/types/programs"
import type {
  SessionData,
  SessionExercise,
  ExerciseInstructions,
  WarmupBlock,
  WarmupExercise,
  CooldownStep,
  SessionFormat,
} from "@/types/session"
import { decodeTempo } from "@/types/session"
import { parseTestProtocol } from "@/lib/strengthTest"
import { parseSetScheme } from "@/lib/setScheme"

// ── localStorage pending session ────────────────────────────

const PENDING_SESSION_KEY = "zeyone_pending_session"

export interface PendingSession {
  programme_id: string
  session_number: number
}

export function savePendingSession(data: PendingSession): void {
  try {
    localStorage.setItem(PENDING_SESSION_KEY, JSON.stringify(data))
  } catch {
    // ignore storage errors
  }
}

export function getPendingSession(): PendingSession | null {
  try {
    const raw = localStorage.getItem(PENDING_SESSION_KEY)
    if (!raw) return null
    return JSON.parse(raw) as PendingSession
  } catch {
    return null
  }
}

export function clearPendingSession(): void {
  try {
    localStorage.removeItem(PENDING_SESSION_KEY)
  } catch {
    // ignore
  }
}

// ── Supabase helpers ────────────────────────────────────────

export async function getProgramMeta(
  programmeId: string
): Promise<{ sessions_total: number; duration_weeks: number; testing_sessions: number[] } | null> {
  const { data, error } = await supabase
    .from("programs")
    .select("sessions_total, duration_weeks, testing_sessions")
    .eq("programme_id", programmeId)
    .maybeSingle()
  if (error || !data) return null
  return {
    sessions_total: data.sessions_total as number,
    duration_weeks: data.duration_weeks as number,
    testing_sessions: Array.isArray(data.testing_sessions)
      ? (data.testing_sessions as number[]).filter((n) => Number.isFinite(n))
      : [],
  }
}

/**
 * Semaine du bloc où tombe chacune des séances demandées.
 *
 * `programs.testing_sessions` donne des NUMÉROS DE SÉANCE (4, 20, 48…), pas des semaines.
 * Les déduire d'une division par le nombre de séances hebdomadaires serait faux : les
 * programmes n'ont pas tous le même rythme et une semaine de décharge peut en compter
 * moins. La correspondance exacte est déjà en base, dans `program_sessions.week`.
 *
 * Renvoie une Map vide si le programme est inconnu — l'appelant traite ce cas comme
 * « échéance non identifiable », jamais comme une erreur.
 */
export async function getSessionWeeks(
  programmeId: string,
  sessionNumbers: number[]
): Promise<Map<number, number>> {
  if (sessionNumbers.length === 0) return new Map()
  const { data, error } = await supabase
    .from("program_sessions")
    .select("session_number, week")
    .eq("programme_id", programmeId)
    .in("session_number", sessionNumbers)

  if (error || !data) {
    console.error("[getSessionWeeks] error:", error)
    return new Map()
  }
  return new Map(data.map((r) => [r.session_number as number, r.week as number]))
}

/**
 * Numéros des séances d'une semaine du bloc, triés — de quoi situer une séance dans sa
 * semaine (« J2 sur 4 »). Même source que `getSessionWeeks` : le rythme hebdomadaire
 * n'est pas le même d'un programme à l'autre, on ne le déduit donc pas d'une division.
 *
 * Renvoie un tableau vide en cas d'erreur — l'aperçu n'affiche alors que la semaine.
 */
export async function getWeekSessionNumbers(
  programmeId: string,
  week: number
): Promise<number[]> {
  const { data, error } = await supabase
    .from("program_sessions")
    .select("session_number")
    .eq("programme_id", programmeId)
    .eq("week", week)

  if (error || !data) return []
  return data.map((r) => r.session_number as number).sort((a, b) => a - b)
}

/**
 * Première séance du bloc en cours pour ce programme : `wizard_submissions.program_start_session`
 * — 1 par défaut, 5 après un changement de niveau (semaine 1 d'adaptation sautée, réservée
 * aux clients qui démarrent avec Zeyone). Sert de PLANCHER à `getNextSessionNumber` et
 * `getProgramProgress` ci-dessous : les séances avant lui ne sont jamais « la prochaine ».
 *
 * La même colonne est lue par les deux fonctions qui planifient (`check-and-populate-next-block`,
 * qui l'écrit à 5 lors de la transition, et `populate-weekly-calendar`). Avant, ce plancher se
 * DÉDUISAIT du plus petit numéro planifié dans `calendar_events`, pendant que ces fonctions
 * repartaient de 1 : après les séances 5-8, la vague suivante replantait 1-4, le plancher
 * tombait à 1 et l'accueil annonçait le test « Testing » de la séance 1 au lieu de la séance 9.
 * Une décision enregistrée une fois ne dépend plus de ce que contient le calendrier.
 *
 * Ne s'applique qu'au programme COURANT du client (celui du wizard) : pour tout autre
 * `programmeId`, plancher 1. Repli sur 1 aussi si la lecture échoue — c'est le
 * comportement d'un compte qui démarre, jamais une séance sautée à tort.
 */
async function getSessionNumberFloor(userId: string, programmeId: string): Promise<number> {
  const { data, error } = await supabase
    .from("wizard_submissions")
    .select("level_id, goal_id, place, gender, program_start_session")
    .eq("user_id", userId)
    .maybeSingle()

  if (error || !data) return 1
  const current = buildProgrammeId(data.level_id, data.goal_id, data.place, data.gender)
  if (current !== programmeId) return 1
  return (data.program_start_session as number | null) ?? 1
}

/**
 * Avancement du client dans son programme, en une seule requête.
 *
 * Deux nombres, et ils ne disent PAS la même chose :
 *   — `completedCount` compte les séances terminées. C'est le compteur « 33 / 35 » ;
 *   — `nextSessionNumber` est la première séance NON faite **à partir de la séance de
 *     départ du bloc** (voir `getSessionNumberFloor`), donc celle qui reste à ouvrir.
 *     C'est elle qui situe le client sur le calendrier de tests : un client qui a
 *     sauté la séance 4 (un test) pour enchaîner sur la 5 en a quatre au compteur,
 *     mais son prochain test est toujours le numéro 4, pas le suivant.
 *
 * Même critère de complétion que `getNextSessionNumber` : un retour post-séance AVEC un
 * RPE (rpe null = questionnaire partiel, séance non validée). On dédoublonne par numéro —
 * un client qui remplit deux fois le même questionnaire ne fait pas avancer le compteur
 * deux fois.
 */
export async function getProgramProgress(
  userId: string,
  programmeId: string
): Promise<{ completedCount: number; nextSessionNumber: number }> {
  const [{ data, error }, floor] = await Promise.all([
    supabase
      .from("session_feedback")
      .select("programme_session_number")
      .eq("user_id", userId)
      .eq("programme_id", programmeId)
      .not("programme_session_number", "is", null)
      .not("rpe", "is", null),
    getSessionNumberFloor(userId, programmeId),
  ])

  if (error || !data) return { completedCount: 0, nextSessionNumber: floor }

  const done = new Set(data.map((r) => r.programme_session_number as number))
  let next = floor
  while (done.has(next)) next++
  return { completedCount: done.size, nextSessionNumber: next }
}

export async function getNextSessionNumber(
  userId: string,
  totalSessions: number,
  programmeId: string
): Promise<number> {
  const [{ data, error }, floor] = await Promise.all([
    supabase
      .from("session_feedback")
      .select("programme_session_number")
      .eq("user_id", userId)
      .eq("programme_id", programmeId)
      .not("programme_session_number", "is", null)
      .not("rpe", "is", null),
    getSessionNumberFloor(userId, programmeId),
  ])

  if (error || !data || data.length === 0) return floor

  const completed = new Set(
    data.map((r) => r.programme_session_number as number).filter(Boolean)
  )
  for (let n = floor; n <= totalSessions; n++) {
    if (!completed.has(n)) return n
  }
  // Tout est fait — reprend au plancher (pas nécessairement 1, cf. ci-dessus).
  return floor
}

export async function getProgramSession(
  programmeId: string,
  sessionNumber: number
): Promise<ProgramSession | null> {
  const { data, error } = await supabase
    .from("program_sessions")
    .select("*")
    .eq("programme_id", programmeId)
    .eq("session_number", sessionNumber)
    .maybeSingle()
  if (error || !data) return null
  return data as ProgramSession
}

// ── ID builder from wizard data ─────────────────────────────

export function getProgrammeIdFromWizard(wizardData: WizardSubmissionRow): string {
  return buildProgrammeId(
    wizardData.level_id,
    wizardData.goal_id,
    wizardData.place,
    wizardData.gender
  )
}

// ── Parsing utilities ───────────────────────────────────────

/** "90''" → 90 | "3 min" → 180 | "120'' (après…)" → 120 | null → 0 */
function parseRestSeconds(s: string | null | undefined): number {
  if (!s) return 0
  const min = /(\d+)\s*min/.exec(s)
  if (min) return parseInt(min[1]) * 60
  const sec = /(\d+)''/.exec(s)
  if (sec) return parseInt(sec[1])
  const bare = /^(\d+)$/.exec(s.trim())
  if (bare) return parseInt(bare[1])
  return 0
}

/**
 * Un item d'échauffement, quelle que soit sa forme : la chaîne historique
 * ("Pull Up 2x10"), découpée ici, ou l'objet migré, qui porte déjà nom, reps et
 * `exercise_id`. Le découpage vient de `parseWarmupLabel` — la même fonction que celle
 * qu'utilise la migration, pour que l'id résolu porte bien sur le nom affiché.
 */
function parseWarmupItem(item: WarmupItem): WarmupExercise {
  if (typeof item !== "string") {
    // La variante peut vivre dans l'un ou l'autre champ : en fin de reps sur un item
    // d'activation ("2x12/côté [Variante…]"), en fin de nom sur une consigne cardio.
    const n = splitWarmupVariant(item.name)
    const r = splitWarmupVariant(item.reps ?? "")
    return {
      name: n.main,
      reps: r.main,
      ...(item.exercise_id ? { exerciseId: item.exercise_id } : {}),
      ...(n.variant || r.variant ? { variant: n.variant ?? r.variant! } : {}),
    }
  }
  const { main, variant } = splitWarmupVariant(item)
  return { ...parseWarmupLabel(main), ...(variant ? { variant } : {}) }
}

/** Libellé complet d'un item, les deux formes réunies. */
function warmupItemText(item: WarmupItem): string {
  return typeof item === "string" ? item : [item.name, item.reps].filter(Boolean).join(" ")
}

/** "Rameur 5 min — intensité modérée" → "5 min" */
function parseDurationFromCardio(s: string | null | undefined): string {
  if (!s) return "5 min"
  const m = /(\d+)\s*min/.exec(s)
  return m ? `${m[1]} min` : "5 min"
}

function inferFormat(formatTravail: string | null | undefined): SessionFormat {
  const f = (formatTravail ?? "").toLowerCase()
  if (f.includes("deload"))                         return "deload"
  if (f.includes("testing") || f.includes("1rm"))  return "testing"
  if (f.includes("rest-pause") || f.includes("rp ")) return "rest-pause"
  if (f.includes("superset"))                       return "superset"
  if (f.includes("bi-set") || f.includes("biset")) return "biset-dropset"
  if (f.includes("circuit") || f.includes("amrap")) return "circuit"
  return "straight-sets"
}

/**
 * Durée estimée de la séance. Le retour au calme est compté pour ce que le programme
 * en prescrit réellement (`cooldownMin`, somme des durées de son texte) et non plus
 * pour 5 minutes forfaitaires : sur un cooldown de 10 min, la séance durait 5 minutes
 * de plus que la carte ne l'annonçait.
 *
 * L'échauffement, lui, reste forfaitaire à 15 min — ses blocs ne portent pas de durée
 * déclarée dans le JSON.
 */
function estimateDuration(exercises: Exercise[], cooldownMin: number): string {
  let totalSec = 0
  for (const ex of exercises) {
    const sets = typeof ex.sets === "number" ? ex.sets : parseInt(String(ex.sets)) || 3
    const rest = parseRestSeconds(ex.rest_between_sets)
    totalSec += sets * (35 + rest)
  }
  const warmupSec = 15 * 60
  const cooldownSec = (cooldownMin > 0 ? cooldownMin : 5) * 60
  return `${Math.round((warmupSec + totalSec + cooldownSec) / 60)} min`
}

// ── Mappers ─────────────────────────────────────────────────

/**
 * Frontière entre le catalogue et l'écran de séance.
 *
 * `sets` et `reps` arrivent en nombre ou en texte selon la séance ("reps": 10 ici,
 * "30''" ou "8+4+3" là). Tout l'aval les traite en texte — `reps.trim()`,
 * `parseRepsCount`, l'écriture dans `session_set_logs.reps` (colonne `text`) — donc
 * ils sont normalisés ICI, une fois, et plus jamais ensuite.
 *
 * `sets` était déjà converti, `reps` non : un `reps` numérique faisait tomber
 * l'écran de séance entier sur « t.trim is not a function ».
 */
function asText(value: string | number): string {
  return typeof value === "number" ? String(value) : value
}

function mapExercise(ex: Exercise): SessionExercise {
  const restSec = parseRestSeconds(ex.rest_between_sets)
  const restExSec = parseRestSeconds(ex.rest_between_exercises)

  const testProtocol = parseTestProtocol(ex.test_protocol) ?? undefined
  const setSchemeSpec = parseSetScheme(ex.set_scheme) ?? undefined

  const instructions: string[] = []
  if (ex.notes)           instructions.push(ex.notes)
  if (ex.female_note)     instructions.push(`Note femme : ${ex.female_note}`)
  if (ex.home_substitute) instructions.push(`Substitut maison : ${ex.home_substitute}`)
  if (instructions.length === 0)
    instructions.push("Exécution soignée, contrôle à chaque répétition.")

  return {
    order:               ex.order,
    name:                ex.name,
    canonicalName:       normalizeExerciseName(ex.name), // overridden by enrichExercisesWithVideos when exercise_id resolves
    exerciseId:          ex.exercise_id,
    category:            ex.category,
    primaryMuscle:       ex.muscle_main,
    secondaryMuscle:     ex.muscle_secondary,
    sets:                asText(ex.sets),
    reps:                asText(ex.reps),
    tempo:               ex.tempo,
    tempoExplain:        decodeTempo(ex.tempo),
    charge:              ex.load,
    restBetweenSets:     restSec,
    restBetweenExercises: restExSec,
    restNature:          ex.rest_type || (restSec === 0 ? "Enchaîner" : "Passive"),
    restExplain:         ex.rest_type || "",
    // Protocole de test déclaré par le JSON. `parseTestProtocol` renvoie null sur tout
    // contenu douteux, et l'écran retombe alors sur le protocole par niveau.
    ...(testProtocol ? { testProtocol } : {}),
    // Schéma de série déclaré par le JSON — méthodes composées uniquement. Absent (ou
    // refusé par la validation) = séries droites, pas de ligne de paliers à l'écran.
    ...(setSchemeSpec ? { setSchemeSpec } : {}),
    instructions,
  }
}

function mapWarmup(warmup: Warmup): WarmupBlock[] {
  const blocks: WarmupBlock[] = []

  if (warmup.mobilite?.length > 0) {
    blocks.push({
      label:     "Mobilité articulaire",
      duration:  "5 min",
      exercises: warmup.mobilite.map(parseWarmupItem),
    })
  }

  if (warmup.cardio) {
    // Le cardio s'affiche en ENTIER ("Rameur 5 min — intensité modérée") : c'est une
    // consigne, pas un nom suivi de reps. Seul l'`exercise_id` de la forme migrée est
    // repris du découpage.
    const cardio = parseWarmupItem(warmup.cardio)
    const full = splitWarmupVariant(warmupItemText(warmup.cardio))
    blocks.push({
      label:     "Cardio",
      duration:  parseDurationFromCardio(full.main),
      exercises: [{ ...cardio, name: full.main, reps: "" }],
    })
  }

  if (warmup.activation?.length > 0) {
    blocks.push({
      label:     "Activation musculaire",
      duration:  "5 min",
      exercises: warmup.activation.map(parseWarmupItem),
    })
  }

  return blocks
}

/**
 * Retour au calme, à partir du seul texte que le programme en donne
 * (`program_sessions.cooldown`, ex. "10 min marche rapide tapis (7-8 km/h)").
 *
 * Le libellé porte le CONTENU, pas le nom de l'étape : "Retour au calme" figure déjà
 * en en-tête de section, et le répéter en dessous ne disait rien de ce qu'il y a à
 * faire. La durée de tête est retirée du libellé pour la même raison — elle vit dans
 * le badge —, et la durée affichée additionne toutes celles du texte : un
 * "5 min vélo + 5 min rameur" dure 10 minutes, pas 5.
 *
 * `(R9)` est un renvoi de règle interne au programme, pas une consigne au client.
 */
/**
 * Premier qualificatif d'intensité ou d'allure. Ce qui suit dit COMMENT faire
 * l'activité, pas laquelle : le résumé de l'aperçu s'arrête là.
 *
 * Pas de `\b` en fin de motif : en ASCII, un mot terminé par "é" ("intensité") n'a pas
 * de frontière de mot avant l'espace suivant, et le qualificatif passait à travers.
 */
const COOLDOWN_QUALIFIER =
  /(^|\s)(intensité|allure|lente?|légère?|léger|leger|très|doux|douce|rapide|modérée?s?|modéré|basse|faible|progressive?s?|progressif|soutenue?s?|équivalente?)(\s|$)/i

/** Nom d'activité d'un segment : "3-5 min rameur intensité modérée" → "rameur". */
function cooldownActivity(segment: string): string {
  let s = segment
    .replace(/\([^)]*\)/g, " ")                         // "(7-8 km/h)", "(clôture du programme)"
    .replace(/^\s*\d+(?:\s*[-–]\s*\d+)?\s*min\s*/i, "") // durée de tête, "3-5 min" compris
    .replace(/\d+(?:\s*[-–]\s*\d+)?\s*km\/h/gi, " ")    // allure chiffrée
  const cut = COOLDOWN_QUALIFIER.exec(s)
  if (cut) s = s.slice(0, cut.index)
  return s.replace(/\s+(sur|à|au)\s*$/i, "").replace(/\s{2,}/g, " ").replace(/\s*\/\s*/g, "/").trim()
}

/**
 * Résumé pour l'aperçu — les activités enchaînées, rien d'autre : "Vélo puis Rameur".
 *
 * Uniquement sur un texte à PLUSIEURS activités. Un texte simple ("Vélo intensité
 * basse") est déjà court, et le raccourcir encore lui ferait perdre l'allure sans rien
 * gagner : `null`, et l'aperçu affiche le libellé tel quel.
 */
function cooldownSummary(main: string): string | null {
  const segments = main.split(/\s*\+\s*/)
  if (segments.length < 2) return null
  const names = segments
    .map(cooldownActivity)
    .filter(Boolean)
    .map(n => n.charAt(0).toUpperCase() + n.slice(1))
  return names.length > 1 ? names.join(" puis ") : null
}

function mapCooldown(cooldown: string | null): CooldownStep[] {
  if (!cooldown) return []
  const { main, variant } = splitWarmupVariant(cooldown)
  const minutes = [...main.matchAll(/(\d+)\s*min/g)].reduce((acc, m) => acc + parseInt(m[1], 10), 0)
  const duration = minutes > 0 ? `${minutes} min` : parseDurationFromCardio(main)
  const content = main
    .replace(/\s*\(R\d+\)/g, "")
    .replace(/^\s*\d+\s*min\s*/i, "")
    .trim()
  // Un texte qui ne dit qu'une durée n'a pas de contenu à afficher : on garde alors le
  // texte brut plutôt que d'inventer une description.
  const label = content ? content.charAt(0).toUpperCase() + content.slice(1) : main
  const summary = cooldownSummary(main.replace(/\s*\(R\d+\)/g, "").trim())
  return [{
    label,
    ...(summary ? { summary } : {}),
    duration,
    detail: cooldown,
    ...(variant ? { variant } : {}),
  }]
}

/** Compute totalSets from exercises array */
function computeTotalSets(exercises: Exercise[]): number {
  return exercises.reduce((acc, ex) => {
    const n = typeof ex.sets === "number" ? ex.sets : parseSetsCount(ex.sets)
    return acc + n
  }, 0)
}

/** Extract display name from label: "S37 — Push (Sem 10) — …" → "Push" */
/**
 * Le nom affiché d'une séance : son TYPE, tel que le catalogue le déclare
 * (`program_sessions.type`) — « Upper », « Push », « Testing 1RM »…
 *
 * Auparavant, le nom était extrait du LIBELLÉ en coupant sur le tiret et en
 * gardant le deuxième morceau. Ça marchait pour les 1 156 libellés de la forme
 * « S2 — Adaptation Upper », mais 302 autres n'ont pas ce préfixe : pour
 * « Adaptation Upper (Sem 1) — Découverte mouvements barre vide », le deuxième
 * morceau est la DESCRIPTION, et c'est elle qui s'affichait au calendrier.
 *
 * Le type est déclaré sur les 1 458 séances du catalogue, sans exception : il n'y
 * a plus rien à deviner à partir d'une chaîne de caractères.
 *
 * ⚠️ Cette règle est recopiée dans deux edge functions
 * (`check-and-populate-next-block`, `populate-weekly-calendar`), qui écrivent le
 * titre au moment de planifier. Deno et le bundle Vite ne partagent pas de
 * module : les trois copies doivent rester identiques, sinon l'accueil et le
 * calendrier se remettent à annoncer deux noms différents pour la même séance.
 */
const SESSION_TYPE_FIXUPS: Record<string, string> = {
  // Les deux seules valeurs du catalogue qui ne s'affichent pas telles quelles.
  Arms_Shoulders: "Arms/Shoulders",
  FullBody: "Full Body",
}

export function sessionDisplayName(
  type: string | null | undefined,
  sessionNumber?: number | null,
): string {
  const t = (type ?? "").trim()
  if (t) return SESSION_TYPE_FIXUPS[t] ?? t
  return sessionNumber != null ? `Séance ${sessionNumber}` : "Séance"
}

// ── Exercise name normalization ──────────────────────────────

/**
 * Strip parenthetical, lowercase, remove accents, collapse spaces.
 *
 * Le tiret compte comme une espace : la table écrit "Pull-Up" et "Resistance Band
 * Pull-Apart" là où les programmes écrivent "Pull Up" et "Resistance Band Pull Apart".
 * Comparer les deux caractères tels quels faisait échouer des noms pourtant identiques
 * à un trait d'union près — invisible côté corps de séance, où `exercise_id` résout
 * avant d'en arriver là, mais bloquant pour l'échauffement, qui n'a que cette voie.
 */
function normalizeExName(name: string): string {
  return name
    .split("(")[0]
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
}

import { normalizeExerciseName, parseSetsCount, parseWarmupLabel, splitWarmupVariant } from "./exerciseNormalization"
export {
  normalizeExerciseName,
  computeChargeKg,
  splitCombinedField,
  parseSetsCount,
  resolveEquipment,
  equipmentStepKg,
  roundPalierSeries,
  type AthleteOneRm,
  type AthleteLevel,
  type ChargeDisplay,
  type ChargeMovement,
  type ChargeBasis,
  type LoadEquipment,
} from "./exerciseNormalization"


// ── Video enrichment ────────────────────────────────────────

/**
 * Pour chaque exercice de la séance, résout le nom canonique + vidéo/instructions
 * depuis la table `exercises`.
 *  - Priorité : `exercise_id` (migré par scripts/migrate-exercise-ids.js) — lookup
 *    direct par id, fiable à 100%, plus de matching fuzzy.
 *  - Fallback : `normalizeExerciseName(name)` + matching par nom (comportement
 *    historique), pour les exercices pas encore migrés.
 * Retourne les exercices enrichis — jamais de throw, les exercices sans
 * correspondance sont retournés inchangés (avec `canonicalName` déjà posé par
 * mapExercise() comme fallback minimal).
 */
type ExRow = {
  id: string
  name: string
  video_path: string | null
  instructions: ExerciseInstructions | null
}

export async function enrichExercisesWithVideos(
  exercises: SessionExercise[]
): Promise<SessionExercise[]> {
  try {
    const { data, error } = await supabase
      .from("exercises")
      .select("id, name, video_path, instructions")

    if (error || !data || data.length === 0) return exercises

    const idMap = new Map<string, ExRow>()
    const videoMap = new Map<string, string | null>()
    const instrMap = new Map<string, ExerciseInstructions | null>()
    for (const row of data as ExRow[]) {
      idMap.set(row.id, row)
      const key = normalizeExName(row.name)
      videoMap.set(key, row.video_path)
      instrMap.set(key, row.instructions)
    }

    const publicVideoUrl = (path: string | null): string | undefined => {
      if (!path) return undefined
      const { data: { publicUrl } } = supabase.storage.from("exercise-videos").getPublicUrl(path)
      return publicUrl
    }

    return exercises.map(ex => {
      // ── Priorité : exercise_id résolu (lookup direct, fiable) ────────────
      if (ex.exerciseId) {
        const ids = ex.exerciseId.split(" + ").map(s => s.trim())
        const rows = ids.map(id => idMap.get(id)).filter((r): r is ExRow => !!r)
        if (rows.length === ids.length && rows.length > 0) {
          const canonicalName = rows.map(r => r.name).join(" + ")
          const videoUrl = publicVideoUrl(rows[0].video_path)
          if (rows.length > 1) {
            const instructionsDbParts = rows.map(r => ({ name: r.name, instructions: r.instructions }))
            const videoUrlParts = rows
              .map(r => ({ name: r.name, url: publicVideoUrl(r.video_path) }))
              .filter((p): p is { name: string; url: string } => !!p.url)
            return { ...ex, canonicalName, instructionsDbParts, ...(videoUrlParts.length ? { videoUrlParts } : {}), ...(videoUrl ? { videoUrl } : {}) }
          }
          return {
            ...ex,
            canonicalName,
            ...(rows[0].instructions ? { instructionsDb: rows[0].instructions } : {}),
            ...(videoUrl ? { videoUrl } : {}),
          }
        }
        if (import.meta.env.DEV)
          console.warn("[enrichExercises] exercise_id invalide, fallback nom:", ex.name, ex.exerciseId)
      }

      // ── Fallback : normalizeExerciseName() + matching fuzzy par nom ──────
      const resolved = ex.canonicalName

      // Combined exercise: "ExoA + ExoB"
      if (resolved.includes(" + ")) {
        const parts = resolved.split(" + ")

        const instructionsDbParts = parts.map(partName => {
          let k = normalizeExName(partName)
          if (!instrMap.has(k)) {
            for (const dbKey of instrMap.keys()) {
              if (k === dbKey || k.startsWith(dbKey + " ")) { k = dbKey; break }
            }
          }
          return { name: partName, instructions: instrMap.get(k) ?? null }
        })

        const hasSome = instructionsDbParts.some(p => p.instructions !== null)
        if (!hasSome) {
          if (import.meta.env.DEV)
            console.warn("[enrichExercises] combined unresolved:", ex.name)
          return ex
        }

        // Video: une par partie
        const videoUrlParts = parts.flatMap(partName => {
          let vk = normalizeExName(partName)
          if (!videoMap.has(vk)) {
            for (const dbKey of videoMap.keys()) {
              if (vk === dbKey || vk.startsWith(dbKey + " ")) { vk = dbKey; break }
            }
          }
          const vPath = videoMap.get(vk) ?? null
          const url = publicVideoUrl(vPath)
          return url ? [{ name: partName, url }] : []
        })
        let enriched: SessionExercise = { ...ex, instructionsDbParts }
        if (videoUrlParts.length) {
          enriched = { ...enriched, videoUrlParts, videoUrl: videoUrlParts[0].url }
        }
        return enriched
      }

      // Single exercise
      let key = normalizeExName(resolved)
      let enriched: SessionExercise = ex

      if (!videoMap.has(key) && !instrMap.has(key)) {
        for (const dbKey of videoMap.keys()) {
          if (key === dbKey || key.startsWith(dbKey + " ")) { key = dbKey; break }
        }
      }

      const videoPath = videoMap.get(key) ?? null
      if (videoPath) {
        const { data: { publicUrl } } = supabase.storage
          .from("exercise-videos")
          .getPublicUrl(videoPath)
        enriched = { ...enriched, videoUrl: publicUrl }
      }

      const instructionsDb = instrMap.get(key) ?? null
      if (instructionsDb) {
        enriched = { ...enriched, instructionsDb }
      } else if (import.meta.env.DEV) {
        console.warn("[enrichExercises] unresolved:", ex.name, "→ resolved:", resolved, "→ key:", key)
      }

      return enriched
    })
  } catch {
    return exercises
  }
}

/**
 * Vidéos des exercices d'échauffement (Mobilité, Cardio, Activation). Les items
 * d'échauffement sont de simples chaînes sans `exercise_id` : même résolution que le
 * fallback par nom d'enrichExercisesWithVideos — normalizeExerciseName() puis
 * correspondance exacte ou par préfixe sur la table `exercises`. Pour un combo
 * "ExoA + ExoB", la première partie qui a une vidéo est retenue.
 * Jamais de throw : les blocs sont retournés inchangés en cas d'erreur.
 */
export async function enrichWarmupWithVideos(blocks: WarmupBlock[]): Promise<WarmupBlock[]> {
  if (blocks.length === 0) return blocks
  try {
    const { data, error } = await supabase
      .from("exercises")
      .select("id, name, video_path")
      .not("video_path", "is", null)

    if (error || !data || data.length === 0) return blocks

    const videoMap = new Map<string, string>()
    const byId = new Map<string, string>()
    for (const row of data as { id: string; name: string; video_path: string }[]) {
      videoMap.set(normalizeExName(row.name), row.video_path)
      byId.set(row.id, row.video_path)
    }

    const videoUrlFor = (partName: string): string | undefined => {
      let key = normalizeExName(partName)
      if (!videoMap.has(key)) {
        for (const dbKey of videoMap.keys()) {
          if (key.startsWith(dbKey + " ")) { key = dbKey; break }
        }
      }
      const path = videoMap.get(key)
      if (!path) return undefined
      return supabase.storage.from("exercise-videos").getPublicUrl(path).data.publicUrl
    }

    return blocks.map(b => ({
      ...b,
      exercises: b.exercises.map(e => {
        if (e.videoUrl) return e
        // Priorité à l'`exercise_id` (lookup direct, fiable), comme pour le corps de
        // séance ; repli sur le nom pour les items pas encore migrés.
        const fromId = e.exerciseId
          ? e.exerciseId.split(" + ").map(id => byId.get(id.trim())).find((p): p is string => !!p)
          : undefined
        const videoUrl = fromId
          ? supabase.storage.from("exercise-videos").getPublicUrl(fromId).data.publicUrl
          : normalizeExerciseName(e.name)
              .split(" + ")
              .map(videoUrlFor)
              .find((u): u is string => !!u)
        if (!videoUrl && import.meta.env.DEV)
          console.warn("[enrichWarmup] unresolved:", e.name)
        return videoUrl ? { ...e, videoUrl } : e
      }),
    }))
  } catch {
    return blocks
  }
}

// ── Public mapper ───────────────────────────────────────────

export function mapProgramSessionToSessionData(ps: ProgramSession): SessionData {
  const exercises = ps.exercises.map(mapExercise)
  const format    = inferFormat(ps.format_travail)
  const cooldown  = mapCooldown(ps.cooldown)
  const cooldownMin = cooldown.reduce(
    (acc, st) => acc + [...st.duration.matchAll(/(\d+)\s*min/g)].reduce((a, m) => a + parseInt(m[1], 10), 0),
    0,
  )

  return {
    id:                `${ps.programme_id}-${ps.session_number}`,
    sessionNumber:     ps.session_number,
    weekNumber:        ps.week,
    cycleNumber:       ps.cycle,
    cycleName:         `Cycle ${ps.cycle}`,
    format,
    formatLabel:       ps.format_travail ?? "Séries droites",
    displayName:       sessionDisplayName(ps.type, ps.session_number),
    totalSets:         computeTotalSets(ps.exercises),
    estimatedDuration: estimateDuration(ps.exercises, cooldownMin),
    warmup:            mapWarmup(ps.warmup),
    exercises,
    cooldown,
  }
}
